import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DeviceOperationStateItemAttribute } from '../../shared/models/device-operation-state-item-attribute.model';
import { DeviceOperationStateItem } from '../../shared/models/device-operation-state-item.model';
import { DeviceDetailService } from '../../shared/services/device-detail.service';

@Component({
  selector: 'app-device-opstate-items',
  templateUrl: './device-opstate-items.component.html',
  styleUrls: ['./device-opstate-items.component.scss'],
  providers: [
    DeviceDetailService,
    { provide: 'tablePaddingHeight', useValue: 216 },
    { provide: 'itemHeight', useValue: 52 },
    { provide: 'paginations', useValue: [5, 10, 25, 50] },
  ],
})
export class DeviceOpstateItemsComponent implements OnInit, AfterViewInit {
  protected mobile: boolean = false;

  private _selected: DeviceOperationStateItem | undefined;
  public get selected(): DeviceOperationStateItem | undefined {
    return this._selected;
  }
  public set selected(value: DeviceOperationStateItem | undefined) {
    this._selected = value;
    this.deviceOperationStateItemsDetail.data = value?.attributes ?? [];
  }

  @Input() set deviceOperationStateItemsData(
    value: DeviceOperationStateItem[] | undefined
  ) {
    this.deviceOperationStateItems.data = value ?? [];
  }

  protected readonly deviceOperationStateItems: MatTableDataSource<DeviceOperationStateItem> =
    new MatTableDataSource<DeviceOperationStateItem>();
  @ViewChild('deviceOperationStateItemsPaginator', { static: true })
  private deviceOperationStateItemsPaginator!: MatPaginator;
  @ViewChild('deviceOperationStateItemsTable', { static: true })
  private deviceOperationStateItemsSort = new MatSort();
  @ViewChild('deviceOperationStateItemsGroup', { static: true })
  deviceOperationStateItemsGroup: MatButtonToggle | undefined;

  private readonly deviceOperationStateItemsColumns: (keyof DeviceOperationStateItem)[] =
    [
      'operationStateItemNum',
      'nameDisplayText',
      'lastUpdatedTimestamp',
      'lastChangeByMessageId',
    ];
  protected deviceOperationStateItemsColumnsToggle: (keyof DeviceOperationStateItem)[] =
    ['operationStateItemNum', 'nameDisplayText', 'lastUpdatedTimestamp'];
  protected get filteredDeviceOperationStateItemsColumns(): (keyof DeviceOperationStateItem)[] {
    return this.deviceOperationStateItemsColumns.filter((col) =>
      this.deviceOperationStateItemsColumnsToggle.includes(col)
    );
  }

  protected readonly deviceOperationStateItemsDetail: MatTableDataSource<DeviceOperationStateItemAttribute> =
    new MatTableDataSource<DeviceOperationStateItemAttribute>();
  @ViewChild('deviceOperationStateItemsDetailPaginator', { static: true })
  private deviceOperationStateItemsDetailPaginator!: MatPaginator;
  @ViewChild('deviceOperationStateItemsDetailTable', { static: true })
  private deviceOperationStateItemsDetailSort = new MatSort();
  @ViewChild('deviceOperationStateItemsDetailGroup', { static: true })
  deviceOperationStateItemsDetailGroup!: MatButtonToggle;

  private readonly deviceOperationStateItemAttributesColumns: (keyof DeviceOperationStateItemAttribute)[] =
    [
      'operationStateItemAttributeNum',
      'nameDisplayText',
      'valueDisplayText',
      'operationStateItemAttributeType',
      'lastUpdatedTimestamp',
      'lastChangeByMessageId',
    ];
  protected deviceOperationStateItemAttributesColumnsToggle: (keyof DeviceOperationStateItemAttribute)[] =
    ['nameDisplayText', 'valueDisplayText', 'lastUpdatedTimestamp'];
  protected get filteredDeviceOperationStateItemAttributesColumns(): (keyof DeviceOperationStateItemAttribute)[] {
    return this.deviceOperationStateItemAttributesColumns.filter((col) =>
      this.deviceOperationStateItemAttributesColumnsToggle.includes(col)
    );
  }

  @ViewChild('opstateAttrTable', { static: true })
  protected opstateAttrTable!: ElementRef<HTMLTableElement>;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private deviceDetailService: DeviceDetailService
  ) {
    this.deviceOperationStateItems.sortData =
      this.sortDataDeviceOperationStateItems();
    this.deviceOperationStateItemsDetail.sortData =
      this.sortDataDeviceOperationStateItemAttributes();
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (result) => {
        this.mobile = result.matches;
        if (result.matches) {
          this.deviceOperationStateItemsColumnsToggle = [
            'operationStateItemNum',
            'nameDisplayText',
            'lastUpdatedTimestamp',
          ];
          this.deviceOperationStateItemAttributesColumnsToggle = [
            'nameDisplayText',
            'valueDisplayText',
            'lastUpdatedTimestamp',
          ];
        }
        this.calcPagination();
        if (this.selected) {
          this.calcAttrPagination();
        }
      },
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.calcPagination(), 1000);
  }

  ngOnInit(): void {
    this.deviceOperationStateItems.paginator =
      this.deviceOperationStateItemsPaginator;
    this.deviceOperationStateItems.sort = this.deviceOperationStateItemsSort;

    this.deviceOperationStateItemsDetail.paginator =
      this.deviceOperationStateItemsDetailPaginator;
    this.deviceOperationStateItemsDetail.sort =
      this.deviceOperationStateItemsDetailSort;
  }

  private sortDataDeviceOperationStateItems() {
    return (items: DeviceOperationStateItem[], sort: MatSort) => {
      if (!sort.active || sort.direction === '') {
        return items;
      }
      return items.sort(
        (a: DeviceOperationStateItem, b: DeviceOperationStateItem) => {
          const asc = sort.direction === 'asc' ? 1 : -1;
          const valA = a[sort.active as keyof DeviceOperationStateItem] ?? '';
          const valB = b[sort.active as keyof DeviceOperationStateItem] ?? '';
          if (typeof valA === 'number' && typeof valB === 'number') {
            return (valA - valB) * asc;
          }
          return String(valA).localeCompare(String(valB)) * asc;
        }
      );
    };
  }

  private sortDataDeviceOperationStateItemAttributes() {
    return (items: DeviceOperationStateItemAttribute[], sort: MatSort) => {
      if (!sort.active || sort.direction === '') {
        return items;
      }
      return items.sort(
        (
          a: DeviceOperationStateItemAttribute,
          b: DeviceOperationStateItemAttribute
        ) => {
          const asc = sort.direction === 'asc' ? 1 : -1;
          const valA =
            a[sort.active as keyof DeviceOperationStateItemAttribute] ?? '';
          const valB =
            b[sort.active as keyof DeviceOperationStateItemAttribute] ?? '';
          if (typeof valA === 'number' && typeof valB === 'number') {
            return (valA - valB) * asc;
          }
          return String(valA).localeCompare(String(valB)) * asc;
        }
      );
    };
  }

  protected selectOpstateItem(element: DeviceOperationStateItem): void {
    this.selected = element;
    this.calcAttrPagination();
  }

  public selectDeviceOperationStateItem(attributeName: string): void {
    this.selected = this.deviceOperationStateItems.data.find(
      (item) => item.nameDisplayText === attributeName
    );
    setTimeout(() => this.calcAttrPagination(), 100);
  }

  private calcPagination(): void {
    if (this.deviceOperationStateItemsPaginator) {
      this.deviceDetailService.calculatePagination(
        this.deviceOperationStateItemsPaginator
      );
      this.deviceOperationStateItems.paginator =
        this.deviceOperationStateItemsPaginator;
    }
  }

  private calcAttrPagination(): void {
    const tableHeight = this.opstateAttrTable.nativeElement.offsetHeight - 56;
    this.deviceDetailService.calculatePaginationByHeight(
      this.deviceOperationStateItemsDetailPaginator,
      tableHeight
    );
    this.deviceOperationStateItemsDetail.paginator =
      this.deviceOperationStateItemsDetailPaginator;
  }
}
